import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  RootState,
  useRecaptchaToken,
  resetEntertainmentCreditState,
  EntertainmentCreditFormData,
  EntertainmentCreditFormErrors,
  EntertainmentCreditFieldConfig,
  clearRogersFields,
  getDateInputType,
  parseCurrencyValue,
  formatCurrencyInput,
  getRequiredFields,
  buildEntertainmentCreditPayload,
  validateField,
} from '@rb-serve-app/core';
import moment from 'moment';
import Breadcrumbs from '../../components/BreadCrumbs';
import { BalanceIcon } from '../../assets/svg/PaymentStatus/StatusIcon';
import FormDropdown from '../../components/FormDropdown';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { entertainmentCreditRequest } from '@rb-serve-app/core';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// Local type aliases for convenience
// ============================================================================

type FormData = EntertainmentCreditFormData;
type FormErrors = EntertainmentCreditFormErrors;
type FieldConfig = EntertainmentCreditFieldConfig;

interface GlobalConfig {
  requiredSymbol?: string;
  requiredSymbolColor?: string | string[];
  infoTipColor?: string | string[];
}

// ============================================================================
// Helper Functions
// ============================================================================

// Parse and render HTML tags in text (bold, newlines, superscript, paragraphs, lists)
const parseTextWithHTML = (text: string): React.ReactNode => {
  if (!text) return text;

  // Sanitize: only allow b, strong, sup, p, ul, li tags
  const sanitizeAllowedTags = (input: string) => {
    if (!input) return '';
    let out = input;
    // Allow inline tags: b, strong, sup, and block tags: p, ul, li; strip others
    // Using bounded quantifiers to prevent any backtracking issues
    out = out.replace(
      /<\/?(?!b\b|\/b\b|strong\b|\/strong\b|sup\b|\/sup\b|p\b|\/p\b|ul\b|\/ul\b|li\b|\/li\b)[^>]{0,100}>/gi,
      ''
    );
    // Strip attributes from allowed tags
    // Using bounded quantifiers to prevent any backtracking issues
    out = out.replace(/<(b|strong|sup|p|ul|li)(?:\s+[^>]{0,100})?>/gi, '<$1>');
    return out;
  };

  const sanitized = sanitizeAllowedTags(text);

  // Use counter for deterministic keys instead of Math.random()
  let keyCounter = 0;
  const getNextKey = (prefix: string) => `${prefix}-${keyCounter++}`;

  // Parse block-level elements (p, ul) and inline elements (b, strong, sup)
  const parseHTML = (html: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    let currentPos = 0;

    // Regex to match block-level tags (p, ul)
    const blockTagRegex = /<(p|ul)>([\s\S]*?)<\/\1>/gi;
    let blockMatch;

    while ((blockMatch = blockTagRegex.exec(html)) !== null) {
      // Add any text before this block tag
      if (blockMatch.index > currentPos) {
        const textBefore = html.slice(currentPos, blockMatch.index).trim();
        if (textBefore) {
          nodes.push(...parseInline(textBefore, 'text'));
        }
      }

      const tag = blockMatch[1].toLowerCase();
      const content = blockMatch[2];

      if (tag === 'p') {
        nodes.push(
          <p key={getNextKey('p')} className="mb-4">
            {parseInline(content, 'p-content')}
          </p>
        );
      } else if (tag === 'ul') {
        const listItems = parseListItems(content);
        nodes.push(
          <ul key={getNextKey('ul')} className="list-disc pl-6 mb-4 space-y-2">
            {listItems}
          </ul>
        );
      }

      currentPos = blockMatch.index + blockMatch[0].length;
    }

    // Add any remaining text after the last block tag
    if (currentPos < html.length) {
      const remaining = html.slice(currentPos).trim();
      if (remaining) {
        nodes.push(...parseInline(remaining, 'text'));
      }
    }

    // If no block tags were found, just parse as inline
    if (nodes.length === 0) {
      return parseInline(html, 'text');
    }

    return nodes;
  };

  // Parse list items from ul content
  const parseListItems = (ulContent: string): React.ReactNode[] => {
    const items: React.ReactNode[] = [];
    const liRegex = /<li>([\s\S]*?)<\/li>/gi;
    let liMatch;

    while ((liMatch = liRegex.exec(ulContent)) !== null) {
      const content = liMatch[1];
      items.push(
        <li key={getNextKey('li')}>{parseInline(content, 'li-content')}</li>
      );
    }

    return items;
  };

  // Parse inline elements (b, strong, sup) and handle newlines
  const parseInline = (str: string, keyPrefix = 'text'): React.ReactNode[] => {
    // Handle newlines first - split by \n (both escaped \\n and actual \n)
    const lines = str.split(/\\n|\n/);

    const inlineTagRegex = /<(b|strong|sup)>([\s\S]*?)<\/\1>/i;

    const renderLine = (
      line: string,
      linePrefix: string
    ): React.ReactNode[] => {
      const match = inlineTagRegex.exec(line);
      if (!match) {
        return [line];
      }

      const before = line.slice(0, match.index);
      const tag = match[1].toLowerCase();
      const inner = match[2];
      const after = line.slice(match.index + match[0].length);

      const nodes: React.ReactNode[] = [];
      if (before) nodes.push(...renderLine(before, `${linePrefix}-pre`));

      const children = renderLine(inner, `${linePrefix}-${tag}`);
      const elKey = getNextKey(linePrefix);

      switch (tag) {
        case 'b':
        case 'strong':
          nodes.push(<strong key={elKey}>{children}</strong>);
          break;
        case 'sup':
          nodes.push(<sup key={elKey}>{children}</sup>);
          break;
        default:
          nodes.push(<span key={elKey}>{children}</span>);
      }

      if (after) nodes.push(...renderLine(after, `${linePrefix}-post`));
      return nodes;
    };

    // Render each line and add <br /> between them
    return lines.flatMap((line, index) => {
      const rendered = renderLine(line, `${keyPrefix}-line-${index}`);
      return index < lines.length - 1
        ? [...rendered, <br key={`${keyPrefix}-br-${index}`} />]
        : rendered;
    });
  };

  return parseHTML(sanitized);
};

// ============================================================================
// Helper Components
// ============================================================================

const InfoTooltip = ({
  sectionId,
  infoTip,
  isVisible,
  onToggle,
  onClose,
  fillColor,
}: {
  sectionId: string;
  infoTip?: string;
  isVisible: boolean;
  onToggle: () => void;
  onClose: () => void;
  fillColor: string;
}) => {
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  if (!infoTip) return null;

  const handleBlur = () => {
    closeTimerRef.current = setTimeout(onClose, 200);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  return (
    <div className="relative inline-block ml-2">
      <button
        type="button"
        onClick={onToggle}
        onBlur={handleBlur}
        onFocus={cancelClose}
        className="inline-block cursor-pointer"
        aria-label="More information"
      >
        <span role="img" aria-label="Information icon">
          <BalanceIcon fill={fillColor} />
        </span>
      </button>
      {isVisible && (
        <div
          className="absolute left-0 -translate-x-1/2 bottom-full mb-2 z-10 w-64 rounded-md bg-gray-100 px-3 py-2 text-xs text-black shadow-lg border border-gray-400"
          onMouseDown={cancelClose}
          onClick={(e) => e.stopPropagation()}
        >
          {parseTextWithHTML(infoTip || '')}
        </div>
      )}
    </div>
  );
};

const ErrorMessage = ({
  message,
  fieldId,
}: {
  message?: string;
  fieldId?: string;
}) => {
  if (!message) return null;
  return (
    <div
      id={fieldId ? `${fieldId}-error` : undefined}
      className="flex items-center mt-1 gap-1"
      role="alert"
    >
      <BalanceIcon fill="#DA291C" aria-hidden="true" />
      <p className="text-sm text-red-600 whitespace-nowrap">{message}</p>
    </div>
  );
};

const HintText = ({ hint }: { hint?: string }) => {
  if (!hint) return null;
  return (
    <p className="mt-1 text-sm text-gray-600">{parseTextWithHTML(hint)}</p>
  );
};

const FieldLabel = ({
  htmlFor,
  text,
  required,
  symbol,
  color,
  className = 'absolute left-3 top-1 text-xs text-gray-600 pointer-events-none',
}: {
  htmlFor: string;
  text: string;
  required?: boolean;
  symbol?: string;
  color?: string | string[];
  className?: string;
}) => (
  <label htmlFor={htmlFor} className={className}>
    {text}
    {required && symbol && (
      <ColoredText text={symbol} color={color} className="ml-1" />
    )}
  </label>
);

const CalendarIcon = () => (
  <svg
    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const CheckmarkIcon = () => (
  <svg
    className="w-3 h-3 text-red-600"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={3}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    className="w-4 h-4 ml-1"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

// ============================================================================
// Utility Functions
// ============================================================================

const getColorClass = (
  color: string | string[],
  type: 'text' | 'fill' = 'text'
): string => {
  if (!color) return '';

  // If it's a hex color string, return it directly for fill or convert to inline style for text
  if (typeof color === 'string' && color.startsWith('#')) {
    return color; // Return hex color directly
  }

  // Handle array format (legacy support)
  const colorValue = Array.isArray(color) ? color[0] : color;

  // If it's still a hex color after array extraction
  if (colorValue.startsWith('#')) {
    return colorValue;
  }

  // Handle named colors (legacy support)
  const colorMap = {
    text: { RED: 'text-red-600', BLACK: 'text-black', GREY: 'text-gray-500' },
    fill: { RED: '#DA291C', BLACK: '#000000', GREY: '#999999' },
  };

  return colorMap[type][colorValue as keyof typeof colorMap.text] || '';
};

const getFieldErrorClasses = (hasError: boolean): string => {
  return hasError
    ? 'border-red-500 focus:ring-red-500 bg-red-50'
    : 'border-gray-300 focus:ring-blue-500';
};

// Helper to apply color (hex or class) to an element
const applyColor = (
  color: string | string[] | undefined,
  defaultColor = '#DA291C'
) => {
  const colorValue = getColorClass(color || defaultColor);
  const isHex = colorValue?.startsWith('#');

  return {
    className: isHex ? '' : colorValue,
    style: isHex ? { color: colorValue } : undefined,
  };
};

// Helper component for colored text (like asterisks)
const ColoredText = ({
  text,
  color,
  className = '',
}: {
  text: string;
  color?: string | string[];
  className?: string;
}) => {
  const { className: colorClass, style } = applyColor(color);
  return (
    <span className={`${className} ${colorClass}`} style={style}>
      {text}
    </span>
  );
};

// ============================================================================
// Date Utility Functions (web-specific)
// ============================================================================

const getDateSeparator = (format: string): string => {
  const separators = ['/', '-', '.', ' '];
  return separators.find((sep) => format.includes(sep)) || '/';
};

const parseDateByFormat = (dateStr: string, format: string): Date | null => {
  if (!dateStr || !format) return null;

  const separator = getDateSeparator(format);
  const formatParts = format.split(separator);
  const dateParts = dateStr.split(separator);

  if (formatParts.length !== dateParts.length) return null;

  let day = 1,
    month = 1,
    year = 1970;

  formatParts.forEach((part, index) => {
    const value = parseInt(dateParts[index], 10);
    if (isNaN(value)) return null;

    if (part.includes('DD') || part === 'D') day = value;
    else if (part.includes('MM') || part === 'M') month = value;
    else if (part.includes('YYYY')) year = value;
    else if (part.includes('YY')) year = value < 100 ? 2000 + value : value;
  });

  return new Date(year, month - 1, day);
};

/**
 * Converts French/Contentful date format to standard format
 * JJ (jour) -> DD (day)
 * MM (mois) -> MM (month)
 * AAAA (année) -> YYYY (year)
 */
const convertFrenchDateFormat = (contentfulFormat: string): string => {
  if (!contentfulFormat) return 'MM/DD/YYYY';

  return contentfulFormat
    .replace(/JJ/g, 'DD') // French day format
    .replace(/AAAA/g, 'YYYY') // French year format (4 digits)
    .replace(/AA/g, 'YY'); // French year format (2 digits, if used)
  // MM stays the same as it's used in both French and English
};

const convertFromHTML5Format = (
  html5Date: string,
  targetFormat: string
): string => {
  if (!html5Date) return '';

  const parts = html5Date.split('-');
  const replacements: Record<string, string> = {};

  // Convert French format to standard format for processing
  const standardFormat = convertFrenchDateFormat(targetFormat);

  if (parts.length === 2) {
    // Month input: YYYY-MM
    const [year, month] = parts;
    replacements.YYYY = year;
    replacements.AAAA = year; // Support French AAAA
    replacements.YY = year.slice(-2);
    replacements.AA = year.slice(-2); // Support French AA
    replacements.MM = month;
    replacements.M = parseInt(month, 10).toString();
  } else if (parts.length === 3) {
    // Date input: YYYY-MM-DD
    const [year, month, day] = parts;
    replacements.YYYY = year;
    replacements.AAAA = year; // Support French AAAA
    replacements.YY = year.slice(-2);
    replacements.AA = year.slice(-2); // Support French AA
    replacements.DD = day;
    replacements.JJ = day; // Support French JJ
    replacements.MM = month;
  }

  let result = targetFormat;
  Object.entries(replacements).forEach(([key, value]) => {
    result = result.replace(new RegExp(key, 'g'), value);
  });

  return result;
};

const convertToHTML5Format = (
  dateStr: string,
  sourceFormat: string
): string => {
  if (!dateStr) return '';

  const separator = getDateSeparator(sourceFormat);
  const formatParts = sourceFormat.split(separator);
  const dateParts = dateStr.split(separator);

  if (formatParts.length !== dateParts.length) return '';

  let day = '01',
    month = '01',
    year = '1970';

  formatParts.forEach((part, index) => {
    const value = dateParts[index];
    // Support both English and French formats
    if (part.includes('DD') || part.includes('JJ') || part === 'D')
      day = value.padStart(2, '0');
    else if (part.includes('MM') || part === 'M')
      month = value.padStart(2, '0');
    else if (part.includes('YYYY') || part.includes('AAAA')) year = value;
    else if (part.includes('YY') || part.includes('AA')) {
      const yearNum = parseInt(value, 10);
      year = (yearNum < 100 ? 2000 + yearNum : yearNum).toString();
    }
  });

  const inputType = getDateInputType(sourceFormat);
  return inputType === 'month' ? `${year}-${month}` : `${year}-${month}-${day}`;
};

const getDateFormatRegex = (format: string): RegExp => {
  const separator = getDateSeparator(format);
  const escapedSep = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Convert French format to standard for regex generation
  const standardFormat = convertFrenchDateFormat(format);

  const regexStr = standardFormat
    .replace('DD', '(0[1-9]|[12][0-9]|3[01])')
    .replace(/(?<!D)D(?!D)/, '([1-9]|[12][0-9]|3[01])')
    .replace('MM', '(0[1-9]|1[0-2])')
    .replace(/(?<!M)M(?!M)/, '([1-9]|1[0-2])')
    .replace('YYYY', '\\d{4}')
    .replace('YY', '\\d{2}')
    .replace(
      new RegExp(separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      escapedSep
    );

  return new RegExp(`^${regexStr}$`);
};

// ============================================================================
// Main Component
// ============================================================================

const WorldLegendCreditRedemption = () => {
  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries } = contentfulData;

  const rogersEntertainmentCredit =
    entries?.rb_rewards?.rogersEntertainmentCredit;
  const globalConfig = rogersEntertainmentCredit?.globalConfig;
  const sectionNonRogers = rogersEntertainmentCredit?.sectionNonRogers;
  const sectionRogersAccount = rogersEntertainmentCredit?.sectionRogersAccount;
  const sectionTnc = rogersEntertainmentCredit?.sectionTnc;
  const successModal = rogersEntertainmentCredit?.modal?.sucess; // Note: typo in Contentful "sucess"
  const failModal = rogersEntertainmentCredit?.modal?.error;
  const errorsDetails = rogersEntertainmentCredit?.modal?.errors;
  const navigate = useNavigate();
  console.log('errorsDetails', errorsDetails);

  const [showRogersAccountSection, setShowRogersAccountSection] =
    useState(false);
  const [formData, setFormData] = useState<FormData>({
    eligible_service_dropdown: '',
    service_purchase_method_dropdown: '',
    account_email: '',
    rogers_account_number: '',
    rogers_transaction_date: '',
    rogers_transaction_date_transaction_info: '',
    rogers_amount_billed: '',
    rogers_claim_amount: '',
    transaction_date: '',
    amount_billed: '',
    claim_amount: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const getRecaptchaToken = useRecaptchaToken();

  // Get language for currency formatting
  const lang = localStorage.getItem('language') || 'en-US';
  const isFrench = lang === 'fr' || lang === 'fr-CA';

  const breadcrumbs = [
    { name: entries?.rb_navigation?.rewardsLogoTitle, href: '/rewards' },
    {
      name: rogersEntertainmentCredit?.breadcrumb?.name,
      href: '/world-legend-credit-redemption',
    },
  ];

  // Check if breadcrumbs should be shown
  const showBreadcrumbs =
    rogersEntertainmentCredit?.breadcrumb?.isEnabled !== false;

  const accountId = useSelector((state: any) => state.user?.accountId);
  const { loading, submitSuccess, submitError } = useSelector(
    (state: RootState) => state.entertainmentCredit
  );
  const dispatch = useDispatch();

  // Reset any stale submission state from a previous session on mount
  useEffect(() => {
    dispatch(resetEntertainmentCreditState());
  }, []);

  // Toggle Rogers Account section based on dropdown selections
  useEffect(() => {
    const shouldShowRogers =
      formData.service_purchase_method_dropdown === 'rogers_account';

    setShowRogersAccountSection(shouldShowRogers);

    if (!shouldShowRogers) {
      clearRogersFields(setFormData, setErrors);
    }
  }, [
    formData.eligible_service_dropdown,
    formData.service_purchase_method_dropdown,
  ]);
  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleInputChange = (
    fieldId: string,
    value: string | boolean,
    fieldConfig?: FieldConfig
  ) => {
    setFormData((prev) => {
      const updated = { ...prev, [fieldId]: value };

      // Re-validate the linked claim amount field when billed amount changes
      if (fieldId === 'amount_billed' || fieldId === 'rogers_amount_billed') {
        const claimFieldId =
          fieldId === 'rogers_amount_billed'
            ? 'rogers_claim_amount'
            : 'claim_amount';
        const claimValue = parseFloat(updated[claimFieldId] as string);
        const billedValue = parseFloat(value as string);
        if (!isNaN(claimValue) && !isNaN(billedValue)) {
          const claimError =
            claimValue > billedValue
              ? 'Claim amount cannot exceed the billed amount.'
              : '';
          setErrors((prev) => ({ ...prev, [claimFieldId]: claimError }));
        }
      }

      return updated;
    });
    if (fieldConfig) {
      const error = validateField(fieldId, value, fieldConfig, formData);
      setErrors((prev) => ({ ...prev, [fieldId]: error }));
    }
  };

  const handleDateInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldId: string,
    format: string
  ) => {
    const formattedValue = e.target.value
      ? convertFromHTML5Format(e.target.value, format)
      : '';
    setFormData((prev) => ({ ...prev, [fieldId]: formattedValue }));
  };

  const handleFieldBlur = (
    fieldId: string,
    value: string | boolean,
    fieldConfig: FieldConfig
  ) => {
    const error = validateField(fieldId, value, fieldConfig, formData);
    setErrors((prev) => ({ ...prev, [fieldId]: error }));
  };

  const isFormValid = (): boolean => {
    const requiredFields = getRequiredFields(showRogersAccountSection);

    // Only check errors for fields that are active in the current section
    const sectionTnc = rogersEntertainmentCredit?.sectionTnc || [];
    const checkboxIds = sectionTnc
      .filter((item: any) => item.type === 'checkbox' && item.requiredField)
      .map((item: any) => item.id);
    const activeFieldIds = new Set([...requiredFields, ...checkboxIds]);

    if (
      Object.entries(errors).some(
        ([fieldId, error]) => activeFieldIds.has(fieldId) && error
      )
    ) {
      return false;
    }

    const requiredCheckboxes = sectionTnc.filter(
      (item: any) => item.type === 'checkbox' && item.requiredField
    );
    const allCheckboxesChecked = requiredCheckboxes.every(
      (checkbox: any) => formData[checkbox.id] === true
    );
    if (!allCheckboxesChecked) return false;

    return requiredFields.every((field) => {
      const value = formData[field as keyof FormData];
      return value && value !== '';
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      const token = await getRecaptchaToken('entertainment_credit_reversal');
      formData['recaptchaToken'] = token || '';
      console.log('Form Data Submitted:', JSON.stringify(formData, null, 2));

      const payload = buildEntertainmentCreditPayload(
        formData,
        token || '',
        isFrench
      );

      dispatch(
        entertainmentCreditRequest({
          accountId,
          data: payload,
        })
      );
    }
  };
  console.log('submitSuccess', submitSuccess, submitError);
  useEffect(() => {
    if (submitSuccess) {
      setShowSuccessModal(true);
    } else if (submitError) {
      setShowFailModal(true);
    }
  }, [submitSuccess, submitError]);
  // ============================================================================
  // Render Functions - Field Components
  // ============================================================================

  const renderDropdown = (field: FieldConfig) => {
    const hasError = errors[field.id];
    const value = formData[field.id as keyof FormData] as string;

    return (
      <div key={field.id} className="mb-6">
        {field.title && (
          <div className="flex items-center mb-2">
            <h3 className="text-sm font-medium text-gray-900">
              {parseTextWithHTML(field.title)}
            </h3>
            {field.infoTip &&
              renderInfoTooltip(`${field.id}-field`, field.infoTip)}
          </div>
        )}
        <FormDropdown
          id={field.id}
          value={value}
          options={field.options || []}
          placeholder={field.placeholder}
          floatingLabel={field.infoPlaceHolder || field.placeholder}
          onChange={(newValue) => handleInputChange(field.id, newValue, field)}
          onBlur={() => handleFieldBlur(field.id, value, field)}
          error={hasError}
          required={field.requiredField}
          aria-label={field.placeholder}
          aria-required={field.requiredField}
          aria-invalid={Boolean(hasError)}
          aria-describedby={hasError ? `${field.id}-error` : undefined}
        />
        <ErrorMessage message={hasError} fieldId={field.id} />
      </div>
    );
  };

  const renderDateField = (field: FieldConfig, infoTip?: string) => {
    const hasError = errors[field.id];
    const value = formData[field.id as keyof FormData] as string;
    const contentfulFormat = field.dateFormat || 'MM/DD/YYYY';
    const format = convertFrenchDateFormat(contentfulFormat);
    const inputType = getDateInputType(format);
    const hasValue = Boolean(value);

    const handleContainerClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const input = document.getElementById(
        `${field.id}_picker`
      ) as HTMLInputElement;
      if (input) {
        input.focus();
        if ('showPicker' in input && typeof input.showPicker === 'function') {
          try {
            input.showPicker();
          } catch {
            input.click();
          }
        } else {
          input.click();
        }
      }
    };

    const getMaxDate = () => {
      if (field.validation?.maxDate !== 'today') return undefined;

      // Use moment.js to get today's date in local timezone
      const today = moment();
      return inputType === 'month'
        ? today.format('YYYY-MM') // YYYY-MM for month input
        : today.format('YYYY-MM-DD'); // YYYY-MM-DD for date input
    };

    return (
      <div key={field.id} className="mb-6">
        <div className="flex items-center gap-2">
          <div className="max-w-[370px] flex-1">
            <div
              className={`relative cursor-pointer ${hasError ? 'bg-red-50' : ''}`}
              onClick={handleContainerClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleContainerClick(e as any);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`${field.placeholder}${value ? `, selected: ${value}` : ''}`}
            >
              <input
                type={inputType}
                id={`${field.id}_picker`}
                value={
                  value ? convertToHTML5Format(value, contentfulFormat) : ''
                }
                onChange={(e) => handleDateInput(e, field.id, contentfulFormat)}
                onBlur={() => handleFieldBlur(field.id, value, field)}
                max={getMaxDate()}
                aria-label={field.placeholder}
                aria-required={field.requiredField}
                aria-invalid={Boolean(hasError)}
                aria-describedby={hasError ? `${field.id}-error` : undefined}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                style={{ zIndex: 1 }}
                tabIndex={-1}
              />
              <div
                className={`w-full px-3 pt-6 pb-2 border rounded-sm ${getFieldErrorClasses(Boolean(hasError))} ${hasValue ? 'text-gray-900' : 'text-gray-500'} relative pointer-events-none`}
              >
                {hasValue ? value : contentfulFormat}
                <CalendarIcon />
              </div>
              <FieldLabel
                htmlFor={`${field.id}_picker`}
                text={field.placeholder || ''}
                required={
                  field.requiredField && field.placeholderAppendRequiredSymbol
                }
                symbol={globalConfig?.requiredSymbol}
                color={globalConfig?.requiredSymbolColor}
                className="absolute left-3 top-1 text-xs text-gray-600 pointer-events-none z-20"
              />
            </div>
            <ErrorMessage message={hasError} fieldId={field.id} />
            <HintText hint={field.hint} />
          </div>
          {infoTip && (
            <div className="mb-6 md:mb-1">
              {renderInfoTooltip(field.id, infoTip)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInfoTooltip = (sectionId: string, infoTip?: string) => {
    if (!infoTip) return null;

    return (
      <InfoTooltip
        sectionId={sectionId}
        infoTip={infoTip}
        isVisible={tooltipVisible === sectionId}
        onToggle={() =>
          setTooltipVisible(tooltipVisible === sectionId ? null : sectionId)
        }
        onClose={() => setTooltipVisible(null)}
        fillColor={
          applyColor(globalConfig?.infoTipColor).style?.color ||
          getColorClass(globalConfig?.infoTipColor || '#DA291C', 'fill')
        }
      />
    );
  };

  const renderFieldTitle = (field: FieldConfig) => {
    if (!field.title) return null;
    return (
      <div className="flex items-center mb-2">
        <h3 className="text-sm font-medium text-gray-900">
          {parseTextWithHTML(field.title)}
        </h3>
        {field.infoTip && renderInfoTooltip(`${field.id}-field`, field.infoTip)}
      </div>
    );
  };

  const renderInputField = (field: FieldConfig, infoTip?: string) => {
    const hasError = errors[field.id];
    const value = formData[field.id as keyof FormData];

    if (field.type === 'date') {
      return renderDateField(field, infoTip);
    }

    if (field.type === 'number') {
      const showAsterisk =
        field.requiredField && field.placeholderAppendRequiredSymbol;
      const asteriskSymbol = globalConfig?.requiredSymbol || '*';
      const rawValue = value as string;

      // Show formatted value only when field is not focused
      const isFocused = focusedField === field.id;
      const displayValue =
        isFocused || !rawValue
          ? rawValue
          : formatCurrencyInput(rawValue, isFrench);

      // Get error placeholder from Contentful or use default format
      const errorPlaceholder =
        field.errorMessages?.errorPlaceHolder ||
        (isFrench ? 'XXX,XX$' : '$XXX.XX');

      // Show floating label when there's a value or error
      const showFloatingLabel = Boolean(rawValue) || Boolean(hasError);

      return (
        <React.Fragment key={field.id}>
          {renderFieldTitle(field)}
          <div className="mb-6">
            <div className={`relative ${hasError ? 'bg-red-50' : ''}`}>
              {/* Regular placeholder with asterisk - show when empty and no error */}
              {!rawValue && showAsterisk && !hasError && (
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center text-gray-500 text-base">
                  <span>{field.placeholder}</span>
                  <ColoredText
                    text={asteriskSymbol}
                    color={globalConfig?.requiredSymbolColor}
                    className="ml-0.5"
                  />
                </div>
              )}

              <input
                type="text"
                id={field.id}
                value={displayValue}
                onChange={(e) => {
                  const inputVal = e.target.value;
                  // Remove currency formatting to get raw number
                  const cleanVal = parseCurrencyValue(inputVal);

                  // Check maxDigits validation (counts integer part only)
                  // Fallback: 6 digits for amount_billed/rogers_amount_billed, 3 for claim amounts
                  const isClaimAmount =
                    field.id === 'claim_amount' ||
                    field.id === 'rogers_claim_amount';
                  const isBilledAmount =
                    field.id === 'amount_billed' ||
                    field.id === 'rogers_amount_billed';
                  const defaultMaxDigits = isClaimAmount
                    ? 3
                    : isBilledAmount
                      ? 6
                      : undefined;
                  const maxDigits =
                    field.validation?.maxDigits ?? defaultMaxDigits;

                  if (maxDigits !== undefined && cleanVal) {
                    // Split by decimal point and count only integer part digits
                    const integerPart = cleanVal.split('.')[0];
                    if (integerPart.length > maxDigits) {
                      return; // Don't update if integer part exceeds maxDigits
                    }
                  }

                  // Validate it's a valid number with up to 2 decimals
                  if (cleanVal === '' || /^\d*\.?\d{0,2}$/.test(cleanVal)) {
                    handleInputChange(field.id, cleanVal, field);
                  }
                }}
                onFocus={() => {
                  setFocusedField(field.id);
                }}
                onBlur={() => {
                  setFocusedField(null);
                  // Just trigger validation, keep raw value in formData
                  handleFieldBlur(field.id, value, field);
                }}
                placeholder={hasError ? errorPlaceholder : ''}
                autoComplete="off"
                aria-label={field.placeholder}
                aria-required={field.requiredField}
                aria-invalid={Boolean(hasError)}
                aria-describedby={hasError ? `${field.id}-error` : undefined}
                className={`w-full px-3 ${showFloatingLabel ? 'pt-6 pb-2' : 'py-3'} border rounded-sm focus:outline-none focus:ring-2 placeholder:text-gray-500 ${getFieldErrorClasses(Boolean(hasError))}`}
              />

              {/* Floating Label - show when there's a value or error */}
              {showFloatingLabel && (
                <FieldLabel
                  htmlFor={field.id}
                  text={field.placeholder || ''}
                  required={field.requiredField && showAsterisk}
                  symbol={globalConfig?.requiredSymbol}
                  color={globalConfig?.requiredSymbolColor}
                />
              )}
            </div>
            <ErrorMessage message={hasError} fieldId={field.id} />
            <HintText hint={field.hint} />
          </div>
        </React.Fragment>
      );
    }

    if (field.type === 'email' || field.type === 'string') {
      const maxLength =
        field.validation?.maxLengthEnabled && field.validation?.maxLenght
          ? field.validation.maxLenght
          : undefined;

      // Special handling for rogers_account_number - numeric only
      const isNumericField = field.id === 'rogers_account_number';

      return (
        <React.Fragment key={field.id}>
          {renderFieldTitle(field)}
          <div className="mb-6 max-w-[500px]">
            <div className={`relative ${hasError ? 'bg-red-50' : ''}`}>
              <input
                type={field.type === 'email' ? 'email' : 'text'}
                id={field.id}
                value={value as string}
                onChange={(e) => {
                  let inputValue = e.target.value;
                  // For rogers_account_number, only allow numeric characters
                  if (isNumericField) {
                    inputValue = inputValue.replace(/\D/g, '');
                  }
                  handleInputChange(field.id, inputValue, field);
                }}
                onBlur={() => handleFieldBlur(field.id, value, field)}
                placeholder={field.errorMessages?.placeholderEmpty || ''}
                maxLength={maxLength}
                autoComplete="off"
                inputMode={isNumericField ? 'numeric' : undefined}
                aria-label={field.placeholder}
                aria-required={field.requiredField}
                aria-invalid={Boolean(hasError)}
                aria-describedby={hasError ? `${field.id}-error` : undefined}
                className={`w-full px-3 pt-6 pb-2 border rounded-sm focus:outline-none focus:ring-2 ${getFieldErrorClasses(Boolean(hasError))}`}
              />
              <FieldLabel
                htmlFor={field.id}
                text={field.placeholder || ''}
                required={field.requiredField}
                symbol={globalConfig?.requiredSymbol}
                color={globalConfig?.requiredSymbolColor}
              />
            </div>
            <ErrorMessage message={hasError} fieldId={field.id} />
            <HintText hint={field.hint} />
          </div>
        </React.Fragment>
      );
    }

    return null;
  };

  const renderSection = (
    section: any,
    isFirstSection = false,
    rogersSection?: any,
    showTooltip = false
  ) => {
    if (!section) return null;

    // Check if this is a transaction section with amount fields
    const hasAmountFields = section.fields?.some(
      (field: any) =>
        field.id === 'amount_billed' ||
        field.id === 'claim_amount' ||
        field.id === 'rogers_amount_billed' ||
        field.id === 'rogers_claim_amount'
    );

    // Determine which title and infoTip to show for first section
    const shouldShowRogersTitle =
      isFirstSection &&
      showRogersAccountSection &&
      rogersSection?.titleRogersEntertaiment;
    const displayTitle = shouldShowRogersTitle
      ? rogersSection.titleRogersEntertaiment
      : section.title;
    const displayInfoTip = shouldShowRogersTitle
      ? rogersSection.infoTipRogersEntertaiment
      : section.infoTip;

    return (
      <div key={section.id} className="mb-6">
        {displayTitle && (
          <div className="flex items-center mb-4">
            <h2 className="text-base font-medium text-gray-900">
              {parseTextWithHTML(displayTitle)}
              {section.requiredField && (
                <ColoredText
                  text={globalConfig?.requiredSymbol || '*'}
                  color={globalConfig?.requiredSymbolColor}
                  className="ml-1"
                />
              )}
            </h2>
            {showTooltip && renderInfoTooltip(section.id, displayInfoTip)}
          </div>
        )}
        <div>
          {section.inputFields?.map((field: any) => renderDropdown(field))}
          {hasAmountFields ? (
            <div className="-mb-6">
              {/* Render date field first if it exists */}
              {section.fields
                ?.filter((field: any) => field.type === 'date')
                .map((field: any) =>
                  renderInputField(
                    field,
                    field.id === 'transaction_date' ||
                      field.id === 'rogers_transaction_date_transaction_info' ||
                      field.id === 'rogers_transaction_date'
                      ? displayInfoTip
                      : undefined
                  )
                )}

              {/* Render amount fields horizontally - full width */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[1000px]">
                {section.fields
                  ?.filter((field: any) => field.type === 'number')
                  .map((field: any) => renderInputField(field))}
              </div>
            </div>
          ) : (
            section.fields?.map((field: any) =>
              renderInputField(
                field,
                field.id === 'transaction_date' ||
                  field.id === 'rogers_transaction_date_transaction_info' ||
                  field.id === 'rogers_transaction_date'
                  ? displayInfoTip
                  : undefined
              )
            )
          )}
        </div>
      </div>
    );
  };

  if (!rogersEntertainmentCredit) {
    return (
      <div className="bg-bg-body py-4 md:px-8 px-4">
        {showBreadcrumbs && <Breadcrumbs items={breadcrumbs} />}
        <main className="w-full rounded-[15px] bg-white min-h-[70vh]">
          <div className="bg-white md:p-6 p-4 rounded-lg">
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      {showBreadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      <main className="w-full rounded-[15px] bg-white min-h-[70vh]">
        <div className="bg-white md:p-8 p-4 rounded-lg">
          <h1 className="text-[28px] font-semibold leading-[36px] tracking-[0.35%]">
            {parseTextWithHTML(rogersEntertainmentCredit?.title || '')}
          </h1>

          {rogersEntertainmentCredit?.description && (
            <p className="text-sm text-gray-600 mb-4 max-w-[1000px]">
              {parseTextWithHTML(rogersEntertainmentCredit?.description)}
            </p>
          )}

          <form onSubmit={handleSubmit}>
            {/* Service Selection - Always visible */}
            {sectionNonRogers?.[0] &&
              renderSection(
                sectionNonRogers[0],
                true,
                sectionRogersAccount?.[0],
                true
              )}

            {/* Conditional Sections */}
            {showRogersAccountSection ? (
              <>
                {/* Rogers Account Holder Info */}
                {sectionRogersAccount?.[1] &&
                  renderSection(
                    sectionRogersAccount[1],
                    false,
                    undefined,
                    true
                  )}

                {/* Date Charged on Bill */}
                {sectionRogersAccount?.[2] &&
                  renderSection(
                    sectionRogersAccount[2],
                    false,
                    undefined,
                    true
                  )}

                {/* Transaction and Claim Info */}
                {sectionRogersAccount?.[3] &&
                  renderSection(
                    sectionRogersAccount[3],
                    false,
                    undefined,
                    false
                  )}
              </>
            ) : (
              <>
                {/* Non-Rogers Transaction Info */}
                {sectionNonRogers?.[1] &&
                  renderSection(
                    sectionNonRogers[1],
                    true,
                    sectionRogersAccount?.[0],
                    false
                  )}
              </>
            )}

            {/* Terms and Conditions Section */}
            {sectionTnc && sectionTnc.length > 0 && (
              <div>
                {sectionTnc.map((item: any, index: number) => {
                  // Render document review section
                  if (item.type === 'document_review') {
                    return (
                      <div key={item.id || `tnc-${index}`} className="mb-6">
                        <h2 className="text-lg font-semibold mb-4 text-gray-900">
                          {parseTextWithHTML(item.title)}
                        </h2>
                        <div className="flex flex-col gap-2">
                          {showRogersAccountSection &&
                          item.documentRogersAccount
                            ? item.documentRogersAccount?.map(
                                (doc: any, docIndex: number) => (
                                  <a
                                    key={`${doc.id}_${docIndex}`}
                                    href={doc.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${doc.label} (opens in new tab)`}
                                    className="text-blue-600 hover:text-blue-800 underline text-sm flex items-center"
                                  >
                                    {parseTextWithHTML(doc.label)}
                                    <ExternalLinkIcon />
                                  </a>
                                )
                              )
                            : item.documents?.map(
                                (doc: any, docIndex: number) => (
                                  <a
                                    key={`${doc.id}_${docIndex}`}
                                    href={doc.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`${doc.label} (opens in new tab)`}
                                    className="text-blue-600 hover:text-blue-800 underline text-sm flex items-center"
                                  >
                                    {parseTextWithHTML(doc.label)}
                                    <ExternalLinkIcon />
                                  </a>
                                )
                              )}
                        </div>
                      </div>
                    );
                  }

                  // Render checkbox
                  if (item.type === 'checkbox') {
                    return (
                      <div key={item.id || `tnc-${index}`} className="mb-6">
                        <label className="flex items-start cursor-pointer">
                          <div className="relative flex items-center">
                            <input
                              type="checkbox"
                              id={item.id}
                              checked={!!formData[item.id]}
                              onChange={(e) =>
                                handleInputChange(
                                  item.id,
                                  e.target.checked,
                                  item
                                )
                              }
                              aria-label={item.label}
                              aria-required={item.requiredField}
                              aria-invalid={Boolean(errors[item.id])}
                              aria-describedby={
                                errors[item.id] ? `${item.id}-error` : undefined
                              }
                              className="sr-only peer"
                            />
                            <div className="mt-1 w-4 h-4 border border-gray-300 rounded peer-checked:border-red-600 peer-focus:ring-2 peer-focus:ring-red-500 peer-focus:ring-offset-2 flex items-center justify-center">
                              {formData[item.id] && <CheckmarkIcon />}
                            </div>
                          </div>
                          <span className="ml-3 text-sm text-gray-700">
                            {parseTextWithHTML(item.label)}
                            {item.requiredField && (
                              <ColoredText
                                text={globalConfig?.requiredSymbol || '*'}
                                color={globalConfig?.requiredSymbolColor}
                                className="ml-1"
                              />
                            )}
                          </span>
                        </label>
                        {errors[item.id] && (
                          <div className="ml-7">
                            <ErrorMessage
                              message={errors[item.id]}
                              fieldId={item.id}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Render text (privacy notice)
                  if (item.type === 'text') {
                    return (
                      <div
                        key={item.id || `tnc-${index}`}
                        className="mb-6 max-w-[1000px]"
                      >
                        <p className="text-sm text-gray-600">
                          {parseTextWithHTML(item.text)}
                        </p>
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            )}

            {/* Submit Button */}
            <div>
              <div className={`relative ${loading && 'w-[200px]'}`}>
                <button
                  type="submit"
                  disabled={!isFormValid()}
                  className={`py-3 px-8 rounded-sm font-medium transition-colors ${
                    isFormValid()
                      ? `${loading ? 'w-[200px] h-[48px] bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border cursor-not-allowed' : 'bg-[#DA291C] text-white hover:bg-[#b32218] focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'}`
                      : `bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border cursor-not-allowed  ${loading && 'w-[200px] h-[48px]  flex items-center justify-center'}`
                  }`}
                >
                  {loading
                    ? ''
                    : rogersEntertainmentCredit?.submitCTA || 'Submit Request'}
                </button>
                {loading && (
                  <div className="flex justify-center items-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6"></div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && successModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowSuccessModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowSuccessModal(false);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="success-modal-title"
          tabIndex={-1}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="document"
          >
            {/* Modal Body */}
            <div className="p-6 text-center">
              {/* Success Icon - Show only if showSuccessIcon is true */}
              {successModal.showSuccessIcon && (
                <div
                  className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4"
                  style={{
                    backgroundColor: successModal.successIconColor || '#22bb33',
                  }}
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="white"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}

              {/* Title - Show only if title exists */}
              {successModal.title && (
                <h2
                  id="success-modal-title"
                  className="text-xl font-semibold text-gray-900 mb-2"
                >
                  {parseTextWithHTML(successModal.title)}
                </h2>
              )}

              {/* Description - Show only if description exists */}
              {successModal.description && (
                <p
                  className={`text-sm text-gray-600 ${successModal.cta ? 'mb-6' : ''}`}
                >
                  {parseTextWithHTML(successModal.description)}
                </p>
              )}

              {/* CTA Button */}
              {successModal.cta && (
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    dispatch(resetEntertainmentCreditState());
                    navigate('/rewards');
                  }}
                  className="w-full py-3 px-6 bg-[#DA291C] text-white font-medium rounded-sm hover:bg-[#b32218] transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {successModal.cta}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fail Modal */}
      {showFailModal && failModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowFailModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowFailModal(false);
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="fail-modal-title"
          tabIndex={-1}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="document"
          >
            {/* Modal Body */}
            <div className="p-6 text-center">
              {/* Error Icon - Show only if showErrorIcon is true */}
              {failModal.showErrorIcon && (
                <div
                  className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4"
                  style={{
                    backgroundColor: failModal.errorIconColor || '#FF0000',
                  }}
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="white"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              )}

              {/* Title - Show only if title exists */}
              {failModal.title && (
                <h2
                  id="fail-modal-title"
                  className="text-xl font-semibold text-gray-900 mb-2"
                >
                  {parseTextWithHTML(failModal.title)}
                </h2>
              )}

              {/* Description - Show only if description exists */}
              {failModal.description && (
                <p
                  className={`text-sm text-gray-600 ${failModal.cta ? 'mb-6' : ''}`}
                >
                  {submitError?.errorCode
                    ? errorsDetails[submitError?.errorCode]?.description ||
                      parseTextWithHTML(failModal.description)
                    : parseTextWithHTML(failModal.description)}
                </p>
              )}

              {/* CTA Button */}
              {failModal.cta && (
                <button
                  onClick={() => {
                    setShowFailModal(false);
                    dispatch(resetEntertainmentCreditState());
                    navigate('/rewards');
                  }}
                  className="w-full py-3 px-6 bg-[#DA291C] text-white font-medium rounded-sm hover:bg-[#b32218] transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {failModal.cta}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldLegendCreditRedemption;
